'use client'

import { useFailedToolRuns } from '@/hooks/use-failed-tool-runs'
import { FailedToolRuns } from '@/components/marketing/tools/failed-tool-runs'
import { toolFailureMessage } from '@/lib/tool-generations'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import {
  ArrowRight,
  Upload,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Zap,
  Loader2,
  X,
} from 'lucide-react'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PricingDialog } from '@/components/billing/pricing-dialog'
import { FreePreviewResultBanner } from '@/components/marketing/tools/free-preview'
import { CreditBadge } from '@/components/marketing/tools/credit-badge'
import { useToolCreditTopUp } from '@/components/marketing/tools/use-tool-credit-top-up'
import { SeedanceMiniAuthDialog } from '@/components/marketing/tools/seedance-mini-auth-dialog'
import { ToolWorkspacePicker } from '@/components/marketing/tools/tool-workspace-picker'
import { useAuthContext } from '@/hooks/use-auth-context'
import { revalidateCreditBalance } from '@/hooks/use-billing'
import { useToolWorkspaceOwner } from '@/hooks/use-tool-workspace-owner'
import {
  isAppSumoPlan,
  isTopPlan as isTopPlanSlug,
} from '@/lib/billing/top-plan'
import { useCreditStore } from '@/stores/credit-store'
import { useBillingDialogStore } from '@/stores/billing-dialog-store'
import { track } from '@/lib/analytics/track'
import { getBackendAuthToken } from '@/lib/backend-auth-token'
import { assetMediaSrc } from '@/lib/tool-generation-src'
import {
  SEEDANCE_MINI_FORM_KEY as FORM_KEY,
  SEEDANCE_MINI_HISTORY_KEY as HISTORY_KEY,
  SEEDANCE_MINI_PENDING_KEY as PENDING_KEY,
  mergeServerHistory,
  parseFalToolPayload,
  stampFalToolPayload,
} from '@/lib/fal-tool-storage'
import {
  hasTerminalFailureWitness,
  recordStatusPollFailure,
} from '@/lib/tool-generation-recovery'
import {
  useSeedanceMini,
  CREDITS_PER_SECOND,
  creditCostFrom,
  loadCreditsPerSecond,
  handoffToCanvas,
  historyDeliveryMode,
  loadStoredGenerations,
} from '@/hooks/use-seedance-mini'

const PAGE_PATH = '/models/seedance-2-mini'
// The job in flight. Credits are deducted at submit, so a job whose request
// id lives only in React state is one refresh away from money spent on a
// result nobody can reach. See fal-tool-studio for the full rationale.
const EST_TIME = 'Generation usually takes 2–3 minutes.'
const DEFAULT_PROMPT =
  'A cinematic product reveal on a clean studio set, soft key light, slow dolly-in'

const CDN = 'https://cdn.soku.ai/marketing/videos/tools/seedance-2-mini'

const demos = [
  {
    label: 'Cinematic Landscape',
    desc: 'A lone figure on an endless salt flat at dusk — text-to-video with natural depth and atmosphere',
    video: `${CDN}/sample-a.mp4`,
  },
  {
    label: 'Atmospheric Night',
    desc: 'A warmly lit cabin in a snowy pine forest — falling snow, ambient glow, native audio',
    video: `${CDN}/sample-d.mp4`,
  },
  {
    label: 'Culinary ASMR',
    desc: 'Close-up of sushi rolled by gloved hands — tactile detail with synchronized sound',
    video: `${CDN}/sample-e.mp4`,
  },
]

const REF_LIMITS = {
  image: { max: 9, mb: 30, accept: 'image/*', label: 'Images' },
  video: { max: 3, mb: 30, accept: 'video/*', label: 'Videos' },
  audio: { max: 3, mb: 30, accept: 'audio/*', label: 'Audio' },
} as const

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

type RefFile = { name: string; url: string }

type HistoryItem = {
  id: string
  url: string
  prompt: string
  /** 'preview' only for the experiment's one free watermarked run. Carried by
   *  the server history too, so a reload keeps the upgrade notice up and the
   *  handoff — which has no ledger row to authorize against — disabled. */
  deliveryMode?: 'full' | 'preview' | null
}

export function SeedanceMiniStudio() {
  const [prompt, setPrompt] = useState('')
  const [duration, setDuration] = useState('5s')
  const [resolution, setResolution] = useState('720p')
  const [aspect, setAspect] = useState('9:16')
  const [audioMode, setAudioMode] = useState<'auto' | 'none' | 'reference'>(
    'auto',
  )
  const [refImages, setRefImages] = useState<RefFile[]>([])
  const [refVideos, setRefVideos] = useState<RefFile[]>([])
  const [refAudio, setRefAudio] = useState<RefFile[]>([])
  const [refError, setRefError] = useState<string | null>(null)

  // Demo preview controls (idle state, right column).
  const [activeDemo, setActiveDemo] = useState(0)
  const [playing, setPlaying] = useState<number | null>(null)
  const [muted, setMuted] = useState(true)
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([])

  // Generation lifecycle.
  const [phase, setPhase] = useState<
    'idle' | 'starting' | 'generating' | 'done' | 'error'
  >('idle')
  const [progressStage, setProgressStage] = useState('Submitting…')
  // The price the page shows is the price the server charges, because it is
  // literally the server's table. A hardcoded mirror here is what let a
  // repriced endpoint advertise the old number.
  const [pricing, setPricing] =
    useState<Record<string, number>>(CREDITS_PER_SECOND)
  useEffect(() => {
    let cancelled = false
    void loadCreditsPerSecond().then(table => {
      if (!cancelled && table) setPricing(table)
    })
    return () => {
      cancelled = true
    }
  }, [])
  // False only once the balance resolves and says so.
  const toolGenerationAllowed = useCreditStore(s => s.toolGenerationAllowed)
  const generationMode = useCreditStore(s => s.toolGenerationMode)
  const setToolGenerationMode = useCreditStore(s => s.setToolGenerationMode)
  // `preview` = the free-watermarked-preview experiment granted this org one
  // run: the button generates, the result carries a watermark. Falls back to
  // the boolean for a balance payload predating the mode field.
  const previewMode = generationMode === 'preview'
  const planBlocked =
    generationMode === 'blocked' ||
    (generationMode === null && toolGenerationAllowed === false)
  const [elapsed, setElapsed] = useState(0)
  const [history, setHistory] = useState<HistoryItem[]>([])
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const { failedRuns, rememberFailure } = useFailedToolRuns('seedance-mini')

  const pollAliveRef = useRef(false)
  const resumedRef = useRef(false)
  const hydratedRef = useRef(false)
  // Runs the poller completed in this mount; the server-history fetch merges
  // these instead of erasing them (its snapshot predates the completion).
  const locallyCompletedIdsRef = useRef<Set<string>>(new Set())

  const { isLoaded, bootstrapSettled, isSignedIn, getToken, user } =
    useAuthContext()
  const userId = user?.id ?? null
  const {
    activateWorkspace,
    organizations,
    selectedWorkspaceId,
    topUpReturn,
    upgradeReturn,
    workspaceSelectionRequired,
    workspaceSwitchError,
    workspaceSwitching,
  } = useToolWorkspaceOwner({ pagePath: PAGE_PATH })
  const { ensureWorkspace, generate, pollStatus, checkPreviewEligibility } =
    useSeedanceMini()
  const openBilling = useBillingDialogStore(s => s.open)
  const planName = useCreditStore(s => s.planName)
  const planSlug = useCreditStore(s => s.planSlug)
  const isTopPlan = isTopPlanSlug(planSlug, planName)
  // AppSumo lifetime tiers have no plan above them: an empty balance is
  // recovered with a credit pack, never the subscription grid.
  const isAppSumo = isAppSumoPlan(planSlug)
  const { openTopUp, topUpDialog } = useToolCreditTopUp(topUpReturn)
  const router = useRouter()
  const [authOpen, setAuthOpen] = useState(false)
  const openAuth = useCallback(() => {
    router.replace(
      `${PAGE_PATH}?redirect_url=${encodeURIComponent(PAGE_PATH)}`,
      {
        scroll: false,
      },
    )
    setAuthOpen(true)
  }, [router])

  // The experiment exposure: exactly once per page, and only when the
  // treatment could be shown — signed in, plan refuses generation, workspace
  // known. The server deals the arm here and nowhere else; the balance poll
  // only reports it afterwards.
  // Keyed by workspace, not a one-shot flag: switching workspaces switches the
  // org whose plan is being judged, and a stale answer must not be written
  // over the new one. The in-flight guard below discards a response whose
  // workspace is no longer the selected one — otherwise a control org could
  // be shown a free-preview button the server then refuses.
  const exposedWorkspaceRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (!isSignedIn || generationMode !== 'blocked') return
    if (workspaceSelectionRequired || workspaceSwitching) return
    const workspaceId = selectedWorkspaceId ?? null
    if (exposedWorkspaceRef.current === workspaceId) return
    exposedWorkspaceRef.current = workspaceId
    void checkPreviewEligibility(workspaceId).then(mode => {
      if (exposedWorkspaceRef.current !== workspaceId) return
      if (mode === 'preview') setToolGenerationMode('preview')
    })
  }, [
    checkPreviewEligibility,
    generationMode,
    isSignedIn,
    selectedWorkspaceId,
    setToolGenerationMode,
    workspaceSelectionRequired,
    workspaceSwitching,
  ])

  const durationSeconds = parseInt(duration, 10) || 5
  const cost = creditCostFrom(pricing, durationSeconds, resolution)

  // --- Form persistence (survives the login reload + Stripe round-trip) -----
  // One-time hydration from sessionStorage (browser-only, so it must stay in an
  // effect rather than a lazy useState initializer, which would run during SSR
  // and crash / cause a hydration mismatch). It runs once after auth bootstrap
  // confirms the identity that owns the data.
  useEffect(() => {
    if (!bootstrapSettled || hydratedRef.current) return

    try {
      const raw = sessionStorage.getItem(FORM_KEY)
      if (raw) {
        const stored = parseFalToolPayload<Record<string, unknown>>(
          raw,
          userId,
          { allowAnonymousClaim: true },
        )
        if (stored.matches) {
          const f = stored.payload
          if (typeof f.prompt === 'string') setPrompt(f.prompt)
          if (typeof f.duration === 'string') setDuration(f.duration)
          if (typeof f.resolution === 'string') setResolution(f.resolution)
          if (typeof f.aspect === 'string') setAspect(f.aspect)
          if (
            f.audioMode === 'none' ||
            f.audioMode === 'auto' ||
            f.audioMode === 'reference'
          ) {
            setAudioMode(f.audioMode)
          }
          if (Array.isArray(f.refImages)) setRefImages(f.refImages)
          if (Array.isArray(f.refVideos)) setRefVideos(f.refVideos)
          if (Array.isArray(f.refAudio)) setRefAudio(f.refAudio)
        } else {
          sessionStorage.removeItem(FORM_KEY)
        }
      }
      const rawHistory = sessionStorage.getItem(HISTORY_KEY)
      if (rawHistory) {
        const stored = parseFalToolPayload<HistoryItem[]>(rawHistory, userId)
        if (stored.matches && Array.isArray(stored.payload)) {
          setHistory(stored.payload)
        } else {
          sessionStorage.removeItem(HISTORY_KEY)
        }
      }
    } catch {
      /* ignore malformed cache */
    }
    hydratedRef.current = true
  }, [bootstrapSettled, userId])

  // Persist generated clips so a refresh keeps them (only in the history grid).
  useEffect(() => {
    if (!hydratedRef.current) return
    try {
      sessionStorage.setItem(
        HISTORY_KEY,
        JSON.stringify(stampFalToolPayload(history.slice(0, 12), userId)),
      )
    } catch {
      /* ignore quota */
    }
  }, [history, userId])

  useEffect(() => {
    if (!hydratedRef.current) return
    // Refs (data URLs) can be large; persist best-effort and never let a quota
    // error drop the lightweight fields — store those first, refs separately.
    try {
      sessionStorage.setItem(
        FORM_KEY,
        JSON.stringify(
          stampFalToolPayload(
            { prompt, duration, resolution, aspect, audioMode },
            userId,
          ),
        ),
      )
    } catch {
      /* ignore */
    }
    try {
      const withRefs = JSON.stringify(
        stampFalToolPayload(
          {
            prompt,
            duration,
            resolution,
            aspect,
            audioMode,
            refImages,
            refVideos,
            refAudio,
          },
          userId,
        ),
      )
      sessionStorage.setItem(FORM_KEY, withRefs)
    } catch {
      /* refs too big for sessionStorage — lightweight fields already saved */
    }
  }, [
    prompt,
    duration,
    resolution,
    aspect,
    audioMode,
    refImages,
    refVideos,
    refAudio,
    userId,
  ])

  // Elapsed-time ticker while a clip renders.
  useEffect(() => {
    if (phase !== 'generating' && phase !== 'starting') return
    const t = setInterval(() => setElapsed(e => e + 1), 1000)
    return () => clearInterval(t)
  }, [phase])

  useEffect(() => {
    return () => {
      pollAliveRef.current = false
    }
  }, [])

  const handlePickRefs = useCallback(
    async (
      kind: keyof typeof REF_LIMITS,
      fileList: FileList | null,
      current: RefFile[],
      setter: (next: RefFile[]) => void,
    ) => {
      if (!fileList || fileList.length === 0) return
      setRefError(null)
      const { max, mb, label } = REF_LIMITS[kind]
      const files = Array.from(fileList)
      if (current.length + files.length > max) {
        setRefError(`Up to ${max} ${label.toLowerCase()} references.`)
        return
      }
      for (const f of files) {
        if (f.size > mb * 1024 * 1024) {
          setRefError(`Each ${kind} reference must be ≤ ${mb}MB.`)
          return
        }
      }
      try {
        const added = await Promise.all(
          files.map(async f => ({ name: f.name, url: await fileToDataUrl(f) })),
        )
        setter([...current, ...added])
      } catch {
        setRefError('Could not read that file. Please try another.')
      }
    },
    [],
  )

  const clearPending = useCallback(() => {
    try {
      sessionStorage.removeItem(PENDING_KEY)
    } catch {
      /* private mode — the in-memory poll still works */
    }
  }, [])

  const markPending = useCallback(
    (requestId: string, promptUsed: string) => {
      try {
        sessionStorage.setItem(
          PENDING_KEY,
          JSON.stringify(
            stampFalToolPayload({ requestId, prompt: promptUsed }, userId),
          ),
        )
      } catch {
        /* as above */
      }
    },
    [userId],
  )

  const runPoll = useCallback(
    async (requestId: string, promptUsed: string) => {
      pollAliveRef.current = true
      let consecutiveFailures = 0
      // 20 minutes at 3s. Seedance 2.5 regularly runs past the old 4-minute
      // budget, and this page itself says renders take 2-3 minutes — a ceiling
      // that close to the expected runtime reads as failure on a paid clip.
      const maxAttempts = 400
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (!pollAliveRef.current) return
        await new Promise(r => setTimeout(r, 3000))
        if (!pollAliveRef.current) return
        let res
        try {
          res = await pollStatus(requestId)
        } catch {
          res = null
        }
        // A single failed poll is noise; twenty in a row is not. Without this
        // the loop sat on a permanently-unreachable status for the full
        // budget, showing "Submitting…" the whole time — which is exactly what
        // a job stranded by a model-endpoint change looks like, and it read as
        // an app that had simply stopped.
        if (!res) {
          const recovery = recordStatusPollFailure(consecutiveFailures)
          consecutiveFailures = recovery.consecutiveFailures
          if (recovery.shouldPause) {
            setErrorMsg(
              "Lost contact with this job — we can't read its status yet. Come back later and we'll resume from the same paid job.",
            )
            setPhase('error')
            return
          }
          continue
        }
        consecutiveFailures = 0
        if (res.status === 'IN_QUEUE') {
          setProgressStage(
            res.queuePosition != null
              ? `In queue · position ${res.queuePosition}`
              : 'In queue…',
          )
        } else if (res.status === 'IN_PROGRESS') {
          setProgressStage('Rendering…')
        }
        if (res.status === 'COMPLETED' && res.videoUrl) {
          const videoUrl = res.videoUrl
          locallyCompletedIdsRef.current.add(requestId)
          setHistory(h => [
            {
              id: requestId,
              url: videoUrl,
              prompt: promptUsed,
              deliveryMode: res.deliveryMode ?? 'full',
            },
            ...h,
          ])
          setPhase('done')
          clearPending()
          track('seedance_mini_generate_completed', { request_id: requestId })
          return
        }
        if (res.status === 'FAILED' || res.status === 'ERROR') {
          // A free preview was never charged, so "refunded" is both false and
          // the wrong thing to say; its terminal witness is the delivery mode.
          if (!hasTerminalFailureWitness(res)) {
            setProgressStage('Finalizing your credit refund…')
            continue
          }
          void revalidateCreditBalance()
          setErrorMsg(
            toolFailureMessage(res.failure, res.deliveryMode, requestId),
          )
          rememberFailure(requestId, promptUsed, res.deliveryMode, res.failure)
          setPhase('error')
          clearPending()
          track('seedance_mini_generate_failed', { request_id: requestId })
          return
        }
      }
      // The pending marker stays, so reopening the page resumes the poll —
      // which is the only thing that makes "come back later" true.
      setErrorMsg(
        'Still rendering — this one is taking a while. Leave this page open, or come back to it later and the clip will be waiting.',
      )
      setPhase('error')
    },
    [clearPending, pollStatus, rememberFailure],
  )

  // Resume an in-flight job after a reload. Without this, refreshing during a
  // render discarded a paid job: credits were already deducted and fal was
  // already working, but the request id lived only in React state.
  useEffect(() => {
    if (!bootstrapSettled || resumedRef.current) return
    resumedRef.current = true
    type PendingJob = { requestId?: string; prompt?: string }
    let pending: PendingJob | null = null
    try {
      const raw = sessionStorage.getItem(PENDING_KEY)
      if (raw) {
        const stored = parseFalToolPayload<PendingJob>(raw, userId)
        if (stored.matches) {
          pending = stored.payload
        } else {
          sessionStorage.removeItem(PENDING_KEY)
        }
      }
    } catch {
      pending = null
    }
    if (!pending?.requestId) return
    if (history.some(h => h.id === pending.requestId)) {
      clearPending()
      return
    }
    track('fal_tool_generate_resumed', {
      tool: 'seedance-mini',
      request_id: pending.requestId,
    })
    setPhase('generating')
    setProgressStage('Picking up where you left off…')
    void runPoll(pending.requestId, pending.prompt ?? '')
    // Once on mount only; `history` must not restart the poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrapSettled, userId, clearPending, runPoll])

  // --- Permanent history ---------------------------------------------------
  // Server-backed, so it survives closing the tab and its URLs never expire.
  useEffect(() => {
    if (!isSignedIn) return
    let cancelled = false
    void (async () => {
      const token = await getBackendAuthToken(getToken, { skipCache: true })
      if (!token || cancelled) return
      const stored = await loadStoredGenerations(token, 'seedance-mini')
      // An empty server list still replaces the session-seeded grid: the
      // server is authoritative for a signed-in user's history. Runs the
      // poller completed after this fetch left are merged back in.
      if (cancelled) return
      setHistory(prev =>
        mergeServerHistory(
          stored.map(g => ({
            id: g.requestId,
            url: g.url,
            prompt: g.prompt ?? '',
            deliveryMode: historyDeliveryMode(g),
          })),
          prev,
          locallyCompletedIdsRef.current,
        ),
      )
    })()
    return () => {
      cancelled = true
    }
  }, [isSignedIn, getToken])

  // --- Hand off into the product ------------------------------------------
  // The step this surface was missing: the visitor has a clip they paid for,
  // and the only thing worth doing next is opening it where it can be edited,
  // branched and turned into an ad. Every /tools page has had this; these two
  // hand-written Seedance surfaces ended at a download.
  const [handoffBusy, setHandoffBusy] = useState(false)
  const handleOpenInProduct = useCallback(async () => {
    const item = history[0]
    if (!item) return
    setHandoffBusy(true)
    track('seedance_mini_handoff_clicked', { request_id: item.id })
    const token = await getBackendAuthToken(getToken, { skipCache: true })
    const result = token
      ? await handoffToCanvas(token, item.id, item.prompt)
      : null
    if (!result) {
      setErrorMsg('Could not open this in the product. Please try again.')
      setHandoffBusy(false)
      return
    }
    router.push(result.path)
  }, [getToken, history, router])

  const handleGenerate = useCallback(async () => {
    track('seedance_mini_generate_clicked', {
      signed_in: isSignedIn,
      duration: durationSeconds,
      resolution,
      cost,
    })

    if (!isSignedIn) {
      openAuth()
      return
    }

    // The server rejects this with 402 before it looks at the balance; going
    // straight to the plans skips a round trip whose only content is bad news.
    //
    // `plan_gate_blocked`, not `upgrade_clicked`: the visitor asked to
    // generate and the plan refused. Reporting it as an upgrade click turns a
    // wall into apparent buying intent on every dashboard downstream.
    if (planBlocked) {
      track('seedance_mini_plan_gate_blocked', {
        stage: 'pre_submit',
        reason: 'video_requires_creator',
      })
      openBilling('upgrade', upgradeReturn)
      return
    }

    setErrorMsg(null)
    setElapsed(0)
    setProgressStage('Submitting…')
    setPhase('starting')
    const promptUsed = prompt.trim() || DEFAULT_PROMPT
    try {
      if (organizations.isLoaded && organizations.count === 0) {
        await ensureWorkspace()
      }
      const result = await generate({
        workspaceId: selectedWorkspaceId ?? undefined,
        prompt: promptUsed,
        duration: durationSeconds,
        resolution,
        aspectRatio: aspect,
        generateAudio: audioMode !== 'none',
        imageUrls: refImages.map(r => r.url),
        videoUrls: refVideos.map(r => r.url),
        audioUrls: refAudio.map(r => r.url),
      })

      if (result.status === 'unauthenticated') {
        openAuth()
        setPhase('idle')
        return
      }
      if (result.status === 'insufficient') {
        track('seedance_mini_insufficient_credits', {
          needed: result.needed ?? cost,
          balance: result.balance,
          top_plan: isTopPlan,
        })
        void revalidateCreditBalance()
        if (isAppSumo) {
          openTopUp()
          setPhase('idle')
          return
        }
        if (isTopPlan) {
          // Already on the top tier — nothing to upgrade to; credits refresh on renewal.
          setErrorMsg(
            "You're out of credits for this cycle — they refresh on your renewal date.",
          )
          setPhase('idle')
          return
        }
        track('seedance_mini_plan_gate_blocked', {
          stage: 'pre_submit',
          reason: 'out_of_credits',
        })
        openBilling('exhausted', upgradeReturn)
        setPhase('idle')
        return
      }
      if (result.status === 'plan_required') {
        track('seedance_mini_plan_gate_blocked', {
          stage: 'server_402',
          reason: 'video_requires_creator',
          required_plan: result.requiredPlan,
        })
        openBilling('upgrade', upgradeReturn)
        setPhase('idle')
        return
      }
      if (result.status === 'workspace_selection_required') {
        setErrorMsg('Choose a workspace before generating.')
        setPhase('idle')
        return
      }
      if (result.status === 'error') {
        setErrorMsg(result.message)
        setPhase('error')
        return
      }
      track('seedance_mini_generate_started', {
        request_id: result.requestId,
        credits_charged: result.creditsCharged,
      })
      void revalidateCreditBalance()
      setPhase('generating')
      markPending(result.requestId, promptUsed)
      void runPoll(result.requestId, promptUsed)
    } catch {
      setErrorMsg('Something went wrong. Please try again.')
      setPhase('error')
    }
  }, [
    isSignedIn,
    isTopPlan,
    isAppSumo,
    openTopUp,
    durationSeconds,
    resolution,
    cost,
    aspect,
    prompt,
    audioMode,
    refImages,
    refVideos,
    refAudio,
    organizations.isLoaded,
    organizations.count,
    selectedWorkspaceId,
    ensureWorkspace,
    generate,
    openAuth,
    planBlocked,
    openBilling,
    upgradeReturn,
    markPending,
    runPoll,
  ])

  const togglePlay = (index: number) => {
    const video = videoRefs.current[index]
    if (!video) return
    if (playing === index) {
      video.pause()
      setPlaying(null)
    } else {
      videoRefs.current.forEach((v, i) => {
        if (v && i !== index) v.pause()
      })
      void video.play().catch(() => {
        if (video.paused) {
          setPlaying(current => (current === index ? null : current))
        }
      })
      setPlaying(index)
      setActiveDemo(index)
    }
  }

  const busy = phase === 'starting' || phase === 'generating'
  const generateLabel =
    phase === 'starting'
      ? 'Starting…'
      : phase === 'generating'
        ? 'Generating…'
        : !isSignedIn
          ? 'Sign up to Generate'
          : planBlocked
            ? // Only outcome of "Generate" here would be a 402, after the
              // prompt is written. Name the real next step instead.
              'Upgrade to generate'
            : previewMode
              ? // The run is free and the result is watermarked; saying so on
                // the button is the treatment, not decoration.
                'Generate free preview'
              : 'Generate with Soku AI'

  return (
    <div className="border-border bg-card w-full rounded-2xl border p-6 shadow-sm sm:p-8">
      <PricingDialog />
      <SeedanceMiniAuthDialog open={authOpen} onOpenChange={setAuthOpen} />
      {isSignedIn && organizations.isLoaded ? (
        <ToolWorkspacePicker
          workspaces={organizations.data}
          selectedWorkspaceId={selectedWorkspaceId}
          disabled={workspaceSwitching}
          onSelect={activateWorkspace}
        />
      ) : null}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        {/* Left: Controls */}
        <div className="space-y-5">
          <div>
            <p className="text-muted-foreground text-[10px] tracking-[0.35em] uppercase">
              AI Video Generation
            </p>
            <h3 className="text-foreground text-lg font-semibold sm:text-xl">
              Seedance 2.0 Mini Studio
            </h3>
          </div>

          {/* Model Info */}
          <div className="border-border bg-muted/50 rounded-xl border p-4">
            <div className="flex items-center gap-2">
              <span className="bg-primary/10 text-primary inline-flex h-6 items-center gap-1 rounded-full px-2 text-[10px] font-semibold tracking-wider uppercase">
                <Zap className="size-3" /> Mini Tier
              </span>
              <p className="text-muted-foreground text-[10px] tracking-[0.3em] uppercase">
                Model
              </p>
            </div>
            <div className="mt-3">
              <p className="text-foreground text-sm font-semibold">
                Seedance 2.0 Mini (Distilled)
              </p>
              <p className="text-muted-foreground text-xs">
                Up to 720p · 24fps · ~2× faster than Fast · ~half the cost ·
                Native audio
              </p>
            </div>
          </div>

          {topUpDialog}

          {/* Credits (signed in only) */}
          {isLoaded && isSignedIn && (
            <CreditBadge
              cost={cost}
              costLabel="This clip costs"
              planBlocked={planBlocked}
              previewMode={previewMode}
              onBuyCredits={openTopUp}
              onUpgrade={() => {
                // Double quotes and spacing are load-bearing: the literal is
                // matched at source level by `tool-studio-auth-gate.test.ts`.
                // prettier-ignore
                track("seedance_mini_upgrade_clicked", { source: "credit_badge" })
                openBilling('upgrade', upgradeReturn)
              }}
            />
          )}

          {/* Prompt */}
          <div className="border-border bg-muted/50 rounded-xl border p-4">
            <p className="text-muted-foreground text-[10px] tracking-[0.3em] uppercase">
              Video Prompt
            </p>
            <Textarea
              className="mt-3 min-h-[96px] rounded-lg"
              placeholder="Describe the video you want to create — scene, camera movement, lighting, style..."
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
            />
            <p className="text-muted-foreground mt-2 text-xs">
              Same multimodal inputs as Seedance 2.0 — text, image, video, and
              audio references
            </p>
          </div>

          {/* Reference Inputs — full multimodal: images, videos, audio */}
          <div className="border-border bg-muted/50 rounded-xl border p-4">
            <p className="text-muted-foreground text-[10px] tracking-[0.3em] uppercase">
              Reference Inputs
            </p>
            <p className="text-muted-foreground/70 mt-1 text-[11px]">
              Optional — reference them in your prompt as @Image1, @Video1,
              @Audio1.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {[
                {
                  kind: 'image' as const,
                  Icon: Upload,
                  items: refImages,
                  set: setRefImages,
                },
                {
                  kind: 'video' as const,
                  Icon: Play,
                  items: refVideos,
                  set: setRefVideos,
                },
                {
                  kind: 'audio' as const,
                  Icon: Volume2,
                  items: refAudio,
                  set: setRefAudio,
                },
              ].map(({ kind, Icon, items, set }) => {
                const { max, accept, label } = REF_LIMITS[kind]
                return (
                  <Button
                    key={kind}
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    asChild
                  >
                    <label className="cursor-pointer">
                      <Icon className="size-3.5" />
                      {label} ({items.length}/{max})
                      <input
                        type="file"
                        multiple
                        className="sr-only"
                        accept={accept}
                        onChange={e => {
                          void handlePickRefs(kind, e.target.files, items, set)
                          e.target.value = ''
                        }}
                      />
                    </label>
                  </Button>
                )
              })}
            </div>
            {(refImages.length > 0 ||
              refVideos.length > 0 ||
              refAudio.length > 0) && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {[
                  ...refImages.map((f, i) => ({
                    f,
                    i,
                    tag: `Image${i + 1}`,
                    set: setRefImages,
                    items: refImages,
                  })),
                  ...refVideos.map((f, i) => ({
                    f,
                    i,
                    tag: `Video${i + 1}`,
                    set: setRefVideos,
                    items: refVideos,
                  })),
                  ...refAudio.map((f, i) => ({
                    f,
                    i,
                    tag: `Audio${i + 1}`,
                    set: setRefAudio,
                    items: refAudio,
                  })),
                ].map(({ f, i, tag, set, items }) => (
                  <span
                    key={`${tag}-${f.name}`}
                    className="border-border bg-background text-muted-foreground inline-flex max-w-[160px] items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]"
                  >
                    <span className="text-foreground font-medium">@{tag}</span>
                    <span className="truncate">{f.name}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${tag}`}
                      className="text-muted-foreground/60 hover:text-foreground ml-0.5"
                      onClick={() => set(items.filter((_, idx) => idx !== i))}
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {refError && (
              <p className="mt-2 text-[11px] text-amber-600">{refError}</p>
            )}
          </div>

          {/* Configuration Grid */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="border-border bg-muted/50 rounded-xl border p-4">
              <p className="text-muted-foreground mb-3 text-[10px] tracking-[0.3em] uppercase">
                Duration
              </p>
              <div className="flex flex-wrap gap-1.5">
                {['4s', '5s', '8s', '10s', '15s'].map(d => (
                  <Button
                    key={d}
                    variant={duration === d ? 'default' : 'outline'}
                    size="xs"
                    className="rounded-lg"
                    onClick={() => setDuration(d)}
                  >
                    {d}
                  </Button>
                ))}
              </div>
            </div>

            <div className="border-border bg-muted/50 rounded-xl border p-4">
              <p className="text-muted-foreground mb-3 text-[10px] tracking-[0.3em] uppercase">
                Resolution
              </p>
              <div className="flex flex-wrap gap-1.5">
                {['480p', '720p'].map(r => (
                  <Button
                    key={r}
                    variant={resolution === r ? 'default' : 'outline'}
                    size="xs"
                    className="rounded-lg"
                    onClick={() => setResolution(r)}
                  >
                    {r}
                  </Button>
                ))}
              </div>
              <p className="text-muted-foreground/70 mt-2 text-[10px]">
                Need 1080p/2K? Switch to Seedance 2.0 Standard.
              </p>
            </div>

            <div className="border-border bg-muted/50 rounded-xl border p-4">
              <p className="text-muted-foreground mb-3 text-[10px] tracking-[0.3em] uppercase">
                Aspect Ratio
              </p>
              <div className="flex flex-wrap gap-1.5">
                {['9:16', '16:9', '1:1', '4:3', '21:9'].map(a => (
                  <Button
                    key={a}
                    variant={aspect === a ? 'default' : 'outline'}
                    size="xs"
                    className="rounded-lg"
                    onClick={() => setAspect(a)}
                  >
                    {a}
                  </Button>
                ))}
              </div>
            </div>

            <div className="border-border bg-muted/50 rounded-xl border p-4">
              <p className="text-muted-foreground mb-3 text-[10px] tracking-[0.3em] uppercase">
                Audio
              </p>
              <Select
                value={audioMode}
                onValueChange={v =>
                  setAudioMode(v as 'auto' | 'none' | 'reference')
                }
              >
                <SelectTrigger className="rounded-lg text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-generate</SelectItem>
                  <SelectItem value="none">No audio</SelectItem>
                  <SelectItem value="reference">From reference</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Generate */}
          <Button
            className="w-full rounded-full"
            size="lg"
            onClick={handleGenerate}
            disabled={busy || workspaceSwitching || workspaceSelectionRequired}
          >
            {busy ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Zap className="size-4" />
            )}
            {generateLabel}
            {!busy && <ArrowRight className="size-4" />}
          </Button>
          <p className="text-muted-foreground text-center text-[11px]">
            {busy ? `${progressStage} · ~2–3 min` : EST_TIME}
          </p>
          {(workspaceSwitchError ?? errorMsg) ? (
            <p className="text-center text-xs text-amber-600">
              {workspaceSwitchError ?? errorMsg}
            </p>
          ) : null}
        </div>

        {/* Right: progress while generating, else demo samples */}
        <div className="space-y-4">
          {busy ? (
            <div className="border-primary bg-primary/5 w-full rounded-xl border p-4">
              <p className="text-primary text-[10px] tracking-[0.3em] uppercase">
                Your generation
              </p>
              <div className="border-border relative mt-3 flex aspect-video flex-col items-center justify-center gap-3 overflow-hidden rounded-xl border bg-black text-white/70">
                <Loader2 className="size-8 animate-spin" />
                <p className="text-sm">{progressStage}</p>
                <p className="font-mono text-xs tracking-tight text-white/40">
                  {elapsed}s elapsed · usually 2–3 min
                </p>
              </div>
            </div>
          ) : phase === 'done' && history[0] ? (
            <div className="border-primary bg-primary/5 w-full rounded-xl border p-4">
              <p className="text-primary text-[10px] tracking-[0.3em] uppercase">
                Your generation · ready
              </p>
              <p className="text-muted-foreground/70 mt-1 text-xs">
                Saved to your history below.
              </p>
              <div className="border-border relative mt-3 aspect-video overflow-hidden rounded-xl border bg-black">
                <video
                  src={assetMediaSrc(history[0].url)}
                  controls
                  autoPlay
                  loop
                  playsInline
                  className="h-full w-full object-cover"
                />
              </div>
              {history[0].deliveryMode === 'preview' && (
                <div className="mt-3">
                  <FreePreviewResultBanner
                    testId="seedance-mini-preview-banner"
                    onUpgrade={() => {
                      track('seedance_mini_preview_upgrade_clicked', {
                        request_id: history[0].id,
                      })
                      openBilling('upgrade', upgradeReturn)
                    }}
                  />
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                >
                  <a
                    href={assetMediaSrc(history[0].url) ?? history[0].url}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download
                  </a>
                </Button>
                <Button
                  size="sm"
                  className="rounded-full"
                  disabled={
                    handoffBusy ||
                    // A preview has no ledger row for the handoff to authorize
                    // against, and a canvas must not receive the unwatermarked
                    // original anyway.
                    history[0].deliveryMode === 'preview'
                  }
                  onClick={() => void handleOpenInProduct()}
                >
                  {handoffBusy ? 'Opening…' : 'Open in Soku Creatives'}
                </Button>
              </div>
              <p className="text-muted-foreground mt-2 text-xs">
                Keep going in the product — edit this on a canvas, branch
                variants off it, and assemble it into an ad.
              </p>
            </div>
          ) : (
            demos.map((demo, i) => (
              <div
                key={demo.label}
                className={`w-full rounded-xl border p-4 text-left transition-colors ${
                  activeDemo === i
                    ? 'border-primary bg-primary/5'
                    : 'border-border bg-muted/50 hover:border-primary/40'
                }`}
              >
                <p className="text-muted-foreground text-[10px] tracking-[0.3em] uppercase">
                  {demo.label}
                </p>
                <p className="text-muted-foreground/70 mt-1 text-xs">
                  {demo.desc}
                </p>
                <div className="group border-border relative mt-3 aspect-video overflow-hidden rounded-xl border bg-black">
                  <video
                    ref={el => {
                      videoRefs.current[i] = el
                    }}
                    src={demo.video}
                    muted={muted}
                    loop
                    playsInline
                    preload="metadata"
                    className="h-full w-full object-cover"
                    onEnded={() => setPlaying(null)}
                  />
                  <button
                    type="button"
                    className="absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity hover:bg-black/30"
                    onClick={() => togglePlay(i)}
                  >
                    {playing === i ? (
                      <Pause className="size-10 text-white/80" />
                    ) : (
                      <Play className="size-10 text-white/80" />
                    )}
                  </button>
                  {playing === i && (
                    <button
                      type="button"
                      className="absolute right-2 bottom-2 rounded-full bg-black/50 p-1.5 text-white/80 transition-colors hover:bg-black/70"
                      onClick={e => {
                        e.stopPropagation()
                        setMuted(!muted)
                      }}
                    >
                      {muted ? (
                        <VolumeX className="size-4" />
                      ) : (
                        <Volume2 className="size-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* History — generated clips, pinned to the bottom of the card */}
      {history.length > 0 && (
        <div className="border-border mt-6 border-t pt-6">
          <p className="text-muted-foreground text-[10px] tracking-[0.3em] uppercase">
            Your generations
          </p>
          <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {history.map(item => (
              <div
                key={item.id}
                className="border-border bg-muted/30 overflow-hidden rounded-xl border"
              >
                <video
                  src={assetMediaSrc(item.url)}
                  controls
                  loop
                  playsInline
                  className="aspect-video w-full bg-black object-cover"
                />
                <p
                  className="text-muted-foreground truncate px-3 py-2 text-xs"
                  title={item.prompt}
                >
                  {item.prompt}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      <FailedToolRuns runs={failedRuns} />
    </div>
  )
}
